import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/design-system/components/react";
import {
  contentError,
  savePermission,
  type ContentDetail,
  type PermissionInput,
} from "@/services/contentLibrary";

const fieldClass = "w-full rounded border border-border-strong bg-bg p-2 text-sm text-fg";
const lines = (text: string) => [...new Set(text.split(/\s+/).filter(Boolean))];

function usePermissionForm(record: ContentDetail, userId: string) {
  const prior = record.permission?.permission;
  const [status, setStatus] = useState<PermissionInput["status"]>(prior?.status ?? "pending");
  const [evidence, setEvidence] = useState(prior?.evidence_url ?? "");
  const [covered, setCovered] = useState(
    (
      prior?.approved_series ?? [
        ...new Set(record.candidate.cases.flatMap((item) => item.series_uids)),
      ]
    ).join("\n"),
  );
  const [excluded, setExcluded] = useState((prior?.excluded_series ?? []).join("\n"));
  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);
  const attempt = useRef<{ body: string; id: string } | null>(null);
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (change: PermissionInput) => savePermission(record.candidate_id, change),
    onSuccess: async () => {
      setSaved(true);
      await client.invalidateQueries({ queryKey: ["content-library", userId] });
    },
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setSaved(false);
    const fields = {
      expected_revision: record.revision,
      status,
      scope: "public-linkedin",
      evidence_url: evidence.trim(),
      approved_series: lines(covered),
      excluded_series: lines(excluded),
      notes: notes.trim(),
    };
    const body = JSON.stringify(fields);
    if (attempt.current?.body !== body) attempt.current = { body, id: crypto.randomUUID() };
    mutation.mutate({ ...fields, operation_id: attempt.current.id, scope: "public-linkedin" });
  };
  return {
    prior,
    status,
    setStatus,
    evidence,
    setEvidence,
    covered,
    setCovered,
    excluded,
    setExcluded,
    notes,
    setNotes,
    saved,
    mutation,
    submit,
  };
}

export function PermissionForm({ record, userId }: { record: ContentDetail; userId: string }) {
  const {
    status,
    setStatus,
    evidence,
    setEvidence,
    covered,
    setCovered,
    excluded,
    setExcluded,
    notes,
    setNotes,
    saved,
    mutation,
    submit,
  } = usePermissionForm(record, userId);
  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Supplier permission">
      <h3 className="text-base font-semibold">Supplier permission for public LinkedIn use</h3>
      <p className="text-sm text-fg-muted">
        Record the supplier’s written decision for these images. Final wording still needs Clinical
        and Regulatory Science review before publishing.
      </p>
      <label className="block text-sm">
        Decision
        <select
          className={fieldClass}
          value={status}
          onChange={(e) => setStatus(e.target.value as PermissionInput["status"])}
        >
          <option value="pending">Pending</option>
          <option value="confirmed">Permission confirmed</option>
          <option value="declined">Permission declined</option>
        </select>
      </label>
      <label className="block text-sm">
        Approval email or evidence link
        <input
          className={fieldClass}
          type="url"
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          required={status === "confirmed"}
          placeholder="https://…"
        />
      </label>
      <label className="block text-sm">
        Covered series (one per line)
        <textarea
          className={fieldClass}
          rows={3}
          value={covered}
          onChange={(e) => setCovered(e.target.value)}
          required={status === "confirmed"}
        />
      </label>
      <label className="block text-sm">
        Excluded series (one per line)
        <textarea
          className={fieldClass}
          rows={2}
          value={excluded}
          onChange={(e) => setExcluded(e.target.value)}
        />
      </label>
      <label className="block text-sm">
        {record.revision ? "Reason for change" : "Notes"}
        <textarea
          className={fieldClass}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          required={record.revision > 0}
        />
      </label>
      {mutation.isError && (
        <p role="alert" className="text-sm text-warn">
          {contentError(mutation.error)} Refresh the case to load its latest record.
        </p>
      )}
      {saved && (
        <p role="status" className="text-sm">
          Permission record saved.
        </p>
      )}
      <Button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? "Saving…" : "Record permission"}
      </Button>
    </form>
  );
}
